import { useState, useCallback, useMemo } from 'react'

import {
  getPreviewCharLimit,
  textExceedsCharLimit,
  truncateText,
  truncateBlueskyText,
  truncateMastodonText,
  removeLinkFromEndOfText,
} from '../utils'

type Channel =
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'twitter'
  | 'threads'
  | 'bluesky'
  | 'mastodon'
  | 'tiktok'
  | 'youtube'
  | 'pinterest'
  | 'googlebusiness'
  | 'startpage'
  | 'substack'

type UseExpandablePreviewTextOptions = {
  /** The channel/service type */
  channel: Channel
  /** The original text content */
  text: string | undefined
  /** Optional: URL to remove from end of text when displaying */
  linkToRemove?: string | null
  /** Optional: Whether there's a link attachment (affects text display) */
  hasLink?: boolean
  /** Optional: Custom character limit (overrides channel default) */
  customCharLimit?: number
}

type UseExpandablePreviewTextReturn = {
  /** The text to display (truncated or full based on expansion state) */
  textToDisplay: string | undefined
  /** Whether the original text exceeds the character limit */
  isTruncated: boolean
  /** Whether the text is currently expanded */
  isExpanded: boolean
  /** Function to expand the text (one-way, no collapse) */
  expand: () => void
}

/**
 * Hook to manage expandable text in post previews.
 * Handles truncation logic per channel and expansion state.
 *
 * @example
 * ```tsx
 * const { textToDisplay, isTruncated, isExpanded, expand } = useExpandablePreviewText({
 *   channel: 'linkedin',
 *   text: post.text,
 *   linkToRemove: linkText,
 *   hasLink: true,
 * })
 *
 * return (
 *   <>
 *     <Text>{textToDisplay}</Text>
 *     {isTruncated && !isExpanded && (
 *       <UnstyledButton onClick={expand}>...more</UnstyledButton>
 *     )}
 *   </>
 * )
 * ```
 */
export const useExpandablePreviewText = (
  options: UseExpandablePreviewTextOptions,
): UseExpandablePreviewTextReturn => {
  const {
    channel,
    text,
    linkToRemove,
    hasLink = false,
    customCharLimit,
  } = options

  const [isExpanded, setIsExpanded] = useState(false)

  const expand = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const charLimit = useMemo(() => {
    if (customCharLimit !== undefined) {
      return customCharLimit
    }
    return getPreviewCharLimit(channel)
  }, [channel, customCharLimit])

  const { textToDisplay, isTruncated } = useMemo(() => {
    if (!text) {
      return { textToDisplay: undefined, isTruncated: false }
    }

    // Check if text exceeds limit
    // Note: textExceedsCharLimit uses twitter-text for proper counting of URLs, emojis, etc.
    const exceeds = textExceedsCharLimit(text, charLimit)

    // Remove link from text if applicable (consistent across all channels)
    const processedText =
      hasLink && linkToRemove
        ? removeLinkFromEndOfText(text, linkToRemove)
        : text

    // If expanded, show full text (with link already removed)
    if (isExpanded) {
      return { textToDisplay: processedText, isTruncated: exceeds }
    }

    // Truncate based on channel
    let truncated: string | undefined
    switch (channel) {
      case 'bluesky':
        truncated = truncateBlueskyText(processedText, charLimit)
        break
      case 'mastodon':
        truncated = truncateMastodonText(processedText, charLimit)
        break
      default:
        truncated = truncateText(processedText, charLimit)
    }

    return { textToDisplay: truncated, isTruncated: exceeds }
  }, [text, channel, charLimit, isExpanded, hasLink, linkToRemove])

  return {
    textToDisplay,
    isTruncated,
    isExpanded,
    expand,
  }
}
